import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { InvoiceFormModal } from '../components/InvoiceFormModal.js';
import { ReceivablesPanel } from '../components/ReceivablesPanel.js';
import { ClientSearchSelect } from '../components/ClientSearchSelect.js';
import { SkeletonBlock } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { ViewIcon, DeleteIcon } from '../components/ActionIcons.js';
import { ConfirmModal } from './ClientsPage.js';
import { Receipt } from 'lucide-react';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { formatMinorAmount, type InvoiceListItem } from '@1financial/shared';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

export const INVOICE_STATUS_BADGE: Record<string, string> = {
  DRAFT: 'pill-neutral',
  ISSUED: 'pill-info',
  SENT: 'pill-indigo',
  PARTIALLY_PAID: 'pill-warn',
  PAID: 'pill-success',
  OVERDUE: 'pill-danger',
  CANCELLED: 'pill-neutral',
  VOID: 'pill-neutral',
};

const STATUSES = Object.keys(INVOICE_STATUS_BADGE);

export function InvoicesPage() {
  const { canDo } = useCapabilities();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [view, setView] = useUserPreference<'invoices' | 'receivables'>('invoices.view', 'invoices', {
    validate: (value) => value === 'invoices' || value === 'receivables',
  });
  const [deleting, setDeleting] = useState<InvoiceListItem | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useUserPreference('invoices.status', '');
  const [clientFilter, setClientFilter] = useUserPreference('invoices.clientId', '');
  const [clientFilterLabel, setClientFilterLabel] = useUserPreference('invoices.clientLabel', '');
  const { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev } = useTableControls('issueDate', 'desc', [search, statusFilter, clientFilter]);

  const activeFilters: ActiveFilter[] = [
    ...(search ? [{ key: 'search', label: `Search: ${search}` }] : []),
    ...(statusFilter ? [{ key: 'status', label: `Status: ${statusFilter}` }] : []),
    ...(clientFilter ? [{ key: 'client', label: `Client: ${clientFilterLabel || '…'}` }] : []),
  ];

  function removeFilter(key: string) {
    if (key === 'search') setSearch('');
    if (key === 'status') setStatusFilter('');
    if (key === 'client') { setClientFilter(''); setClientFilterLabel(''); }
  }

  function clearAllFilters() {
    setSearch('');
    setStatusFilter('');
    setClientFilter('');
    setClientFilterLabel('');
  }

  const list = useQuery<{ items: InvoiceListItem[]; total: number; nextCursor: string | null }>({
    queryKey: ['invoices', { search, status: statusFilter, client: clientFilter, sortBy, sortOrder, cursor }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (clientFilter) params.set('clientId', clientFilter);
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (cursor) params.set('cursor', cursor);
      const res = await fetch(`/v1/invoices?${params.toString()}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: InvoiceListItem[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/invoices/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setDeleting(null);
    },
  });

  const items = list.data?.items ?? [];

  if (view === 'receivables') {
    return (
      <div className="space-y-4 w-full min-w-0">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><h1 className="text-xl font-semibold text-white">Receivables</h1><p className="text-sm text-muted">Client statements, running balances and overdue amounts</p></div>
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button onClick={() => setView('invoices')} className="px-3 py-1.5 text-sm text-muted hover:text-white">Invoices</button>
            <button onClick={() => setView('receivables')} className="px-3 py-1.5 text-sm bg-bg-hover text-white">Receivables</button>
          </div>
        </header>
        <ReceivablesPanel />
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full min-w-0">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Invoices</h1>
          <p className="text-sm text-muted">{list.data?.total ?? '—'} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button onClick={() => setView('invoices')} className="px-3 py-1.5 text-sm bg-bg-hover text-white">Invoices</button>
            <button onClick={() => setView('receivables')} className="px-3 py-1.5 text-sm text-muted hover:text-white">Receivables</button>
          </div>
          {canDo('billing.view') && canDo('billing.manage') && (
            <button onClick={() => setCreating(true)} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New invoice</button>
          )}
        </div>
      </header>

      {creating && <InvoiceFormModal onClose={() => setCreating(false)} />}

      {deleting && (
        <ConfirmModal
          title="Delete invoice"
          message={`Delete invoice ${deleting.invoiceNumber}? Soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {list.isLoading && <SkeletonBlock />}
      {list.error && <div className="text-sm text-rose-300">Error: {list.error.message}</div>}

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search number, client or engagement…"
        activeFilters={activeFilters}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        {/* OPS 3.5 #11: server-searched picker (old limit=100 prefetch hid
            clients past the first page). */}
        <ClientSearchSelect
          value={clientFilter}
          onChange={(v, c) => {
            setClientFilter(v);
            setClientFilterLabel(c ? c.company ?? c.name : '');
          }}
          placeholder="All clients"
          className="w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          wrapperClassName="w-full sm:w-52"
        />
      </FilterBar>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-bg-card border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-muted font-medium">Number</th>
              <th className="text-left px-4 py-3 text-muted font-medium">Client / Engagement</th>
              <SortableTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <SortableTh label="Issued" sortKey="issueDate" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <SortableTh label="Due" sortKey="dueDate" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <SortableTh label="Total" sortKey="total" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <th className="text-left px-4 py-3 text-muted font-medium">Paid</th>
              <th className="text-right px-4 py-3 text-muted font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && !list.isLoading && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={8} onClearAll={clearAllFilters} />
                : <tr>
                    <td colSpan={8}>
                      <EmptyState icon={Receipt} title="No invoices yet." />
                    </td>
                  </tr>
            )}
            {items.map((inv) => (
              <tr key={inv.id} className="hover:bg-bg-hover">
                <td className="px-4 py-3">
                  <Link to={`/invoices/${inv.id}`} className="text-accent hover:underline font-medium">{inv.invoiceNumber}</Link>
                </td>
                <td className="px-4 py-3">
                  <div className="max-w-[220px] xl:max-w-[32rem] 2xl:max-w-[48rem] truncate text-white" title={inv.clientName ?? undefined}>{inv.clientName ?? '—'}</div>
                  <div className="max-w-[220px] xl:max-w-[32rem] 2xl:max-w-[48rem] truncate text-xs text-muted" title={inv.engagementTitle ?? undefined}>{inv.engagementTitle ?? ''}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${INVOICE_STATUS_BADGE[inv.status] ?? 'bg-bg-hover text-white'}`}>
                    {inv.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted">{fmtDate(inv.issueDate)}</td>
                <td className="px-4 py-3 text-muted">{fmtDate(inv.dueDate)}</td>
                <td className="px-4 py-3 text-white">{formatMinorAmount(inv.total.amount, true)} {inv.total.currency}</td>
                <td className="px-4 py-3 text-muted">
                  {formatMinorAmount(inv.paidAmount, true)} {inv.total.currency}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <ViewIcon to={`/invoices/${inv.id}`} label="View invoice" />
                    {canDo('billing.view') && canDo('billing.manage') && (
                      <DeleteIcon onClick={() => setDeleting(inv)} label="Delete invoice" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={items.length}
          limit={50}
          total={list.data?.total}
          hasNext={!!list.data?.nextCursor}
          onPrev={goPrev}
          onNext={() => goNext(list.data?.nextCursor)}
        />
      </div>
    </div>
  );
}
